import React from 'react';
import { FileJson, Calendar, Trash2, AlertTriangle, CheckCircle2, CopyCheck, XCircle } from 'lucide-react';
import { RoastProfileSection } from '../../roasts/component/RoastProfile';
import { RoastResultSection } from '../../roasts/component/RoastResultSection';
import type { RoastProfileRow } from '../../roasts/component/types';
import { useLanguage } from '../../../features/language/useLanguage';
import { MetadataCard } from './MetadataCard';
import type { StagedRoast } from '../hooks/useArtisanStaging';

interface Props {
    item: StagedRoast;
    canWrite: boolean;
    /** Editing is closed once the batch is being saved. */
    isLocked: boolean;
    onEdit: (rows: RoastProfileRow[]) => void;
    onRemove: () => void;
    onSelectProfile: (id: string) => void;
}

const OUTCOME_STYLES: Record<string, { wrapper: string; Icon: typeof CheckCircle2 }> = {
    saved: { wrapper: 'bg-green-50 border-green-200 text-green-800', Icon: CheckCircle2 },
    duplicate: { wrapper: 'bg-blue-50 border-blue-200 text-blue-800', Icon: CopyCheck },
    failed: { wrapper: 'bg-red-50 border-red-200 text-red-700', Icon: XCircle },
};

/**
 * One staged roast, shown under the tab strip. A file that could not be read gets an
 * explanation and a way out instead of an empty preview.
 */
export const ArtisanStagedSheet: React.FC<Props> = ({
    item,
    canWrite,
    isLocked,
    onEdit,
    onRemove,
    onSelectProfile,
}) => {
    const { t } = useLanguage();

    if (item.status === 'invalid' || !item.parsed) {
        return (
            <div className="bg-white border border-amber-200 border-t-0 rounded-b-lg p-4 sm:p-6 shadow-sm" data-test-id="artisan-staged-invalid">
                <div className="flex items-start gap-4">
                    <div className="h-11 w-11 flex-shrink-0 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 break-all">{item.fileName}</p>
                        <p className="text-sm text-gray-700 mt-1">
                            {t(`artisanSync.stage.reason.${item.errorCode ?? 'parse'}`)}
                        </p>
                        {item.errorDetail && (
                            <p className="text-xs text-gray-500 mt-1 font-mono break-all">{item.errorDetail}</p>
                        )}
                        <p className="text-sm text-gray-500 mt-3">{t('artisanSync.stage.invalidHelp')}</p>
                    </div>
                    <button
                        onClick={onRemove}
                        disabled={isLocked}
                        className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-test-id="artisan-staged-remove"
                    >
                        <Trash2 className="h-4 w-4" />
                        {t('artisanSync.stage.removeFile')}
                    </button>
                </div>
            </div>
        );
    }

    const { parsed } = item;
    const outcome = OUTCOME_STYLES[item.status];

    return (
        <div className="space-y-6">
            <div className="bg-white border border-t-0 rounded-b-lg p-4 sm:p-6 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4 min-w-0">
                    <div className="h-12 w-12 flex-shrink-0 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
                        <FileJson className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-base sm:text-lg font-semibold text-gray-900 break-all">{item.fileName}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                            <Calendar className="h-4 w-4 flex-shrink-0" />
                            {parsed.metadata.roastDate} {parsed.metadata.roastTime}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onRemove}
                    disabled={isLocked}
                    className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-test-id="artisan-staged-remove"
                >
                    <Trash2 className="h-4 w-4" />
                    {t('artisanSync.stage.removeFile')}
                </button>
            </div>

            {outcome && (
                <div className={`p-3 rounded-lg border text-sm flex items-center gap-3 ${outcome.wrapper}`}>
                    <outcome.Icon className="h-4 w-4 flex-shrink-0" />
                    <span>{item.failure || t(`artisanSync.stage.status.${item.status}`)}</span>
                    {item.profileId && (
                        <button
                            onClick={() => onSelectProfile(item.profileId!)}
                            className="ml-auto font-medium underline underline-offset-2"
                        >
                            {t('artisanSync.bulk.viewProfile')}
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <MetadataCard label={t('artisanSync.meta.operator')} value={parsed.metadata.operator || '-'} />
                <MetadataCard label={t('artisanSync.meta.machine')} value={parsed.metadata.machine || '-'} />
                <MetadataCard label={t('artisanSync.meta.beans')} value={parsed.metadata.beans || '-'} />
                <MetadataCard label={t('artisanSync.meta.drumSpeed')} value={parsed.metadata.drumSpeed ? `${parsed.metadata.drumSpeed} RPM` : '-'} />
            </div>

            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 min-w-0">
                <div className="mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{t('artisanSync.upload.profilePreview')}</h3>
                    <p className="text-sm text-gray-500">{t('artisanSync.upload.profilePreviewHint')}</p>
                </div>
                <RoastProfileSection
                    seconds={0}
                    stage="idle"
                    mode="recipe"
                    initialProfile={item.edited ?? parsed.profile}
                    baselineProfile={parsed.profile}
                    onDataChange={onEdit}
                    readOnly={!canWrite || isLocked}
                    stages={parsed.stages}
                />
                <RoastResultSection
                    stages={parsed.stages}
                    onStageChange={() => { }}
                    weightInput={parsed.weightInput}
                    calculatedWeight={parsed.weightOutput || 0}
                    onWeightInputChange={() => { }}
                    weightOutput={parsed.weightOutput}
                    onWeightOutputChange={() => { }}
                    agtronWhole={Number.isFinite(parsed.agtronWhole) ? String(parsed.agtronWhole) : ''}
                    agtronGround={Number.isFinite(parsed.agtronGround) ? String(parsed.agtronGround) : ''}
                    onAgtronChange={() => { }}
                    roastType={parsed.roastType}
                    onRoastTypeChange={() => { }}
                    mode="recipe"
                    readOnly
                />
            </div>
        </div>
    );
};
